/**
 * KAN-865: вид "Доска" CRM целиком (страница /crm и приложение менеджера /m/board):
 * панель BoardToolbar с переключателем "Требуют внимания (N)", канбан
 * CrmKanbanBoard и панель карточки клиента CrmClientPanel.
 * Состояние в адресе (useCrmBoardView): вид доски, активность,
 * ?attention=1 и ?card=<id> (открытая карточка). Число N - attention_count
 * того же запроса доски (общий кеш, второго запроса нет).
 * KAN-874: на телефоне при "Требуют внимания" вместо колонок общий список
 * AttentionList по всем этапам; десктоп остаётся колонками.
 * KAN-880: под панелью строка фильтров лидов (CrmLeadFilters: поиск, менеджер;
 * KAN-909 без флажка stale), общая с таблицей; фильтры в адресе (useCrmLeadFilters) и в
 * запросах доски, порций колонок и списка внимания.
 * KAN-882: переключатель вида доски (?board=) вместо группировки и исхода;
 * "Требуют внимания" (и общий список на телефоне) только в видах managers и
 * funnel: в других видах ?attention=1 из адреса не действует.
 * KAN-905: "Только новые лиды" (useNewLeadsOnly, общий с таблицей): lead_period=1 добавляется
 * к фильтрам и так уходит во все запросы доски (обзор, порции колонок, список
 * внимания); выбранная колонка не сбрасывается.
 * KAN-910: выбранная колонка в адресе (?column=), умолчания вида доски и активности
 * пишутся в адрес (useCrmBoardDefaults): ссылкой можно поделиться.
 * KAN-957: панель (BoardToolbar + CrmLeadFilters) одна и та же в обычном и
 * развёрнутом на весь экран виде (desktop, ?full=1): в развёрнутом она внутри доски
 * (toolbar CrmKanbanBoard), без переключателя вида, как у таблицы (KAN-883).
 */
import { useMemo, type ReactNode } from 'react';
import { CrmLeadFilters } from '@/components/crm/CrmLeadFilters';
import { useNewLeadsOnly } from '@/hooks/useNewLeadsOnly';
import { useCrmBoardDefaults, useCrmBoardView } from '@/hooks/useCrmBoardView';
import { useCrmLeadFilters, useDropRejectedCrmManager } from '@/hooks/useCrmLeadFilters';
import { useCrmFullscreenValue } from '@/hooks/useCrmFullscreen';
import { useCrmPipeline } from '@/hooks/useCrmPipeline';
import { boardHasAttentionFilter } from '@/lib/crmPipeline';
import { boardLeadPeriodParams } from '@/lib/newLeadsOnly';
import type { WorkSurface } from '@/lib/workActions';
import type { CrmPipelineCard } from '@/types/crmPipeline';
import { AttentionList } from './AttentionList';
import { BoardToolbar } from './BoardToolbar';
import { CrmClientPanel } from './CrmClientPanel';
import { CrmKanbanBoard } from './CrmKanbanBoard';

interface CrmBoardSectionProps {
  variant: 'desktop' | 'mobile';
  /** Ссылка из карточки доски на карточку клиента. */
  clientHref: (card: CrmPipelineCard) => string;
  /** Ссылка "Карточка клиента" в панели. */
  panelClientHref?: (clientId: string) => string;
  companyHref?: (companyId: string) => string;
  /** Перед панелью фильтров (переключатель вида). */
  leading?: ReactNode;
  surface?: WorkSurface;
}

export function CrmBoardSection({
  variant,
  clientHref,
  panelClientHref,
  companyHref,
  leading,
  surface,
}: CrmBoardSectionProps): JSX.Element {
  const board = useCrmBoardView();
  useCrmBoardDefaults();
  const leadFilters = useCrmLeadFilters();
  const newLeads = useNewLeadsOnly(board.activity);
  const filters = useMemo(
    () => ({ ...leadFilters.params, ...boardLeadPeriodParams(newLeads.newLeadsOnly, board.activity) }),
    [leadFilters.params, newLeads.newLeadsOnly, board.activity]
  );
  const view = board.boardView;
  const attentionOnly = board.attentionOnly && boardHasAttentionFilter(view);
  const { data, error } = useCrmPipeline(view, board.activity, attentionOnly, filters);
  useDropRejectedCrmManager(error, leadFilters);
  const openCard = (card: CrmPipelineCard) => board.openCard(card.id);
  // KAN-883: выбранная вкладка-колонка телефона: переживает смену фильтров, периода,
  // "Требуют внимания" (доска перемонтируется), сбрасывается только сменой вида доски.
  // KAN-910: выбор в адресе (?column=), setBoardView снимает его при смене вида.
  const columnChoice = board.column;
  const onColumnChoice = board.setColumn;
  // Развёрнута только desktop-доска: на телефоне кнопки нет, панель всегда сверху.
  const expanded = useCrmFullscreenValue() && variant === 'desktop';

  const panel = (withLeading: boolean) => (
    <>
      <div className="flex min-w-0 flex-wrap items-center gap-3">
        {withLeading ? leading : null}
        <BoardToolbar
          board={view}
          onBoardChange={board.setBoardView}
          variant={variant}
          activity={board.activity}
          onActivityChange={board.setActivity}
          onActivityPreset={board.setActivityPreset}
          attentionOnly={board.attentionOnly}
          onAttentionOnlyChange={board.setAttentionOnly}
          attentionCount={data?.attention_count ?? null}
          newLeads={newLeads}
        />
      </div>
      <CrmLeadFilters state={leadFilters} meTestId="board-manager-me" />
    </>
  );

  return (
    <>
      {!expanded && panel(true)}
      {variant === 'mobile' && attentionOnly ? (
        <AttentionList
          board={view}
          activity={board.activity}
          filters={filters}
          clientHref={clientHref}
          companyHref={companyHref}
          onOpenCard={openCard}
        />
      ) : (
        <CrmKanbanBoard
          board={view}
          activity={board.activity}
          attentionOnly={attentionOnly}
          filters={filters}
          surface={surface}
          columnChoice={columnChoice}
          onColumnChoice={onColumnChoice}
          toolbar={expanded ? panel(false) : undefined}
          variant={variant}
          clientHref={clientHref}
          companyHref={companyHref}
          onOpenCard={openCard}
        />
      )}
      <CrmClientPanel
        clientId={board.cardId}
        variant={variant}
        onClose={board.closeCard}
        clientHref={panelClientHref}
        surface={surface}
      />
    </>
  );
}
